import { useQuery } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  Permission,
  ReachProSupportCaseClient,
} from 'src/WebApiController';

export const SUPPORT_CASE_NOTES_QUERY_KEY = 'reachpro-support-case-notes';

export function useReachProSupportCaseNotes(workItemId: number | undefined) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);
  const hasSupportViewPermission = useUserHasAnyOfPermissions(
    Permission.SellerSupportCase_View
  );

  const shouldQuery =
    hasReachProSupportFeature &&
    hasSupportViewPermission &&
    workItemId != null &&
    activeAccountWebClientConfig.activeAccountId != null;

  const query = useQuery({
    queryKey: [SUPPORT_CASE_NOTES_QUERY_KEY, workItemId],
    queryFn: async () => {
      if (!shouldQuery || workItemId == null) {
        return [];
      }

      const notes = await tryInvokeApi(
        async () => {
          return await new ReachProSupportCaseClient(
            activeAccountWebClientConfig
          ).getSupportCaseNotes(workItemId);
        },
        (error) => {
          trackError('ReachProSupportCaseClient.getSupportCaseNotes', error);
        }
      );

      return notes ?? [];
    },
    enabled: shouldQuery,
    refetchInterval: shouldQuery ? 60000 : false,
    refetchOnWindowFocus: false,
  });

  return query;
}
